/**
 * Organisation Theme Provider
 * Provides organisation-specific theming context to all components
 */

'use client';

import React, { createContext, useContext, useEffect } from 'react';
import { useOrganisationTheme } from '@/hooks/useOrganisationTheme';
import { BackendAuth } from '@/lib/auth-backend';

// Create context
// exportiert fuer optionale Nutzung ohne Throw (useEffectiveBackground)
export const OrganisationThemeContext = createContext(null);

/**
 * Organisation Theme Provider Component
 * Wraps the application and provides theme context
 */
export function OrganisationThemeProvider({ children }) {
  // Organisation SOFORT beim ersten Rendern lesen (nicht erst im Effekt).
  // Vorher: erster Render mit organisationId=null -> Theme galt als "geladen" ->
  // children (die ganze Seite) wurden gemountet und luden ihre Daten; dann kam die
  // Organisation, das Theme wurde geladen (Spinner statt children = Seite weg) und
  // die Seite wurde ein ZWEITES Mal gemountet -> jede Seite lud alles doppelt
  // (Befund 30.09.2026). Der Provider wird nur clientseitig gemountet (die Layout-
  // Huelle zeigt bis zur Auth-Pruefung einen ScreenLoader), localStorage ist da.
  const [organisationId, setOrganisationId] = React.useState(() => {
    try {
      const user = BackendAuth.getUser();
      return user && user.organisation ? user.organisation : null;
    } catch {
      return null;
    }
  });

  // Fallback: Benutzer erst nach dem Mounten verfuegbar (z.B. Login im selben Tab)
  useEffect(() => {
    if (organisationId) return;
    const user = BackendAuth.getUser();
    if (user && user.organisation) {
      setOrganisationId(user.organisation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use organisation theme hook
  const themeData = useOrganisationTheme(organisationId);

  // Show loading state while theme is being fetched
  if (themeData.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <OrganisationThemeContext.Provider value={themeData}>
      {children}
    </OrganisationThemeContext.Provider>
  );
}

/**
 * Hook to use organisation theme context
 * @returns {Object} Theme context data
 */
export function useOrganisationThemeContext() {
  const context = useContext(OrganisationThemeContext);

  if (!context) {
    throw new Error('useOrganisationThemeContext must be used within OrganisationThemeProvider');
  }

  return context;
}

/**
 * Higher-order component to inject theme props
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with theme props
 */
export function withOrganisationTheme(Component) {
  return function ThemedComponent(props) {
    const theme = useOrganisationThemeContext();

    return <Component {...props} organisationTheme={theme} />;
  };
}
